import React from "react";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import CurrencyComponent from "./CurrencyComponent";

import { Colors } from "@/theme";
import { getLocalisationVal } from "@/utils/localizationUtil";
import moment from "moment";
import useStyles from "./styles";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    borderBottomColor: Colors.TABLE_HEADER1,
    color: Colors.GRAY_DARK,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: Colors.TABLE_HEADER1,
  },
  "&:nth-of-type(odd)": {
    backgroundColor: Colors.WHITE,
  },
}));

const TableBodyComponent = ({
  localization,
  loading,
  purchaseStocks,
  products,
  stockTypes,
  onAddCost,
}) => {
  const classes = useStyles();

  const sortedStocksByName = [...stockTypes].sort((a, b) =>
    b.name.localeCompare(a.name)
  );
  const localizationWithoutDecimal = {
    ...localization,
    currency_decimal: false,
  };

  const getProductStock = (purchaseStock, product, stockType) =>
    purchaseStock.products
      ?.find((item) => item?.product?.id === product.id)
      ?.stocks?.find((stock) => stock.stock_type?.id === stockType.id);

  const getCellContent = (value, isPrice) => {
    if (!value) return "N/A";
    if (isPrice) {
      return getLocalisationVal(localization, value);
    } else {
      return `${getLocalisationVal(localizationWithoutDecimal, value)}L`;
    }
  };

  if (loading) {
    return (
      <TableBody>
        {Array.apply(null, Array(3))
          .map(function (y, i) {
            return i;
          })
          .map((id) => (
            <TableRow key={`skeleton-${id}`} sx={{ height: 80 }}>
              {Array.apply(null, Array(products.length * 2 + 1))
                .map(function (y, i) {
                  return i;
                })
                .map((cellId) => (
                  <StyledTableCell
                    key={cellId}
                    align={cellId === 0 ? "left" : "center"}
                  >
                    <Skeleton variant="rounded" height={30} />
                  </StyledTableCell>
                ))}
            </TableRow>
          ))}
      </TableBody>
    );
  }

  return (
    <TableBody>
      {purchaseStocks.map((purchaseStock, id) => (
        <StyledTableRow key={`item-${id}`} sx={{ height: 80 }}>
          <StyledTableCell
            align="left"
            sx={{
              position: "sticky",
              left: 0,
              zIndex: 2,
              background: id % 2 === 0 ? Colors.WHITE : Colors.TABLE_HEADER1,
              borderRight: `1px solid ${Colors.GRAY_BORDER1_40}`,
            }}
          >
            <Box>
              {moment.utc(purchaseStock.update_time).local().format("dddd")}
            </Box>
            <Box sx={{ fontSize: 12 }}>
              {moment
                .utc(purchaseStock.update_time)
                .local()
                .format("DD MMM YYYY HH:mm")}
            </Box>
          </StyledTableCell>
          {products.map((product) => (
            <React.Fragment key={`${id}-${product.id}`}>
              <StyledTableCell align="center">
                {!!getProductStock(
                  purchaseStock,
                  product,
                  sortedStocksByName[0]
                ) ? (
                  <CurrencyComponent
                    localization={localization}
                    defaultPrice={
                      getProductStock(
                        purchaseStock,
                        product,
                        sortedStocksByName[0]
                      )?.purchase_price || "0"
                    }
                    onAdd={(price) =>
                      onAddCost(
                        getProductStock(
                          purchaseStock,
                          product,
                          sortedStocksByName[0]
                        ),
                        price
                      )
                    }
                  />
                ) : (
                  getCellContent(
                    getProductStock(
                      purchaseStock,
                      product,
                      sortedStocksByName[0]
                    )?.purchase_price,
                    true
                  )
                )}
              </StyledTableCell>
              <StyledTableCell
                align="center"
                sx={{
                  borderRight: `1px solid ${Colors.GRAY_BORDER1_40}`,
                }}
              >
                {getCellContent(
                  sortedStocksByName.reduce(
                    (total, item) =>
                      total +
                      parseFloat(
                        getProductStock(purchaseStock, product, item)
                          ?.purchase_stock || "0"
                      ),
                    0
                  ),
                  false
                )}
              </StyledTableCell>
            </React.Fragment>
          ))}
        </StyledTableRow>
      ))}
    </TableBody>
  );
};

export default TableBodyComponent;
